'use client';

import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import { HOMEPAGE_MAX_VISIBLE_SAVED } from './constants';
import styles from './styles.module.css';

import { HomepageProductCard } from '@/app/[locale]/(home)/components/HomepageProductCard';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
  ModuleInteractionType,
  moduleOriginParamName,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { getProductCardColumns } from '@/modules/recentlyViewed/helpers';
import { appendParams } from '@/modules/routing/appendParams/appendParams';
import { type LegacySavedProduct } from '@/modules/saves/types';
import { getProductSizeName } from '@/modules/sizes/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  products: LegacySavedProduct[];
};

export function SavesUI({ products }: Props) {
  const tCommon = useTranslations('common');
  const [_, currentUser] = useCurrentUser();

  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  const isSeeMoreButtonVisible = products.length > HOMEPAGE_MAX_VISIBLE_SAVED;

  function handleProductClick(
    selectedProductId: number,
    selectedProductName: string,
    selectedProductPosition: number
  ) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: HomePageModuleNames.Saves,
      itemClicked: selectedProductName,
      itemId: String(selectedProductId),
      itemPosition: selectedProductPosition,
      schemaVersion: '3.0',
    });
  }

  function handleSeeMoreClick() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      {
        moduleName: HomePageModuleNames.Saves,
        interactionType: ModuleInteractionType.SeeMoreClicked,
        schemaVersion: '1.0',
      }
    );
  }

  return (
    <section>
      <ModuleHeader title={tCommon('MySaves')} size={ModuleHeaderSize.Large}>
        {isSeeMoreButtonVisible && (
          <ExtendedLink
            href={appendParams(`${currentUser?.username}/saved/`, [
              moduleOriginParamName,
              HomePageModuleNames.Saves,
            ])}
            onClick={handleSeeMoreClick}
          >
            <Body variant="sm-regular" className={styles.seeMoreText}>
              {tCommon('SeeMore')}
            </Body>
          </ExtendedLink>
        )}
      </ModuleHeader>

      <ScrollableItemRow
        minItemWidth={144}
        itemsPerRow={getProductCardColumns(products.length)}
      >
        {products
          .slice(0, HOMEPAGE_MAX_VISIBLE_SAVED)
          .map((productItem, index) => (
            <li key={productItem.id} className={styles.productListItem}>
              <HomepageProductCard
                id={productItem.id}
                isLiked={productItem.is_liked}
                preview={productItem.preview}
                productHref={`${productItem.slug}?moduleOrigin=${HomePageModuleNames.Saves}`}
                pictures={productItem.pictures}
                displayPrice={productItem.pricing.display_price}
                hasVideo={productItem.has_video}
                size={
                  productItem.sizes &&
                  getProductSizeName(productItem.sizes, {
                    multipleSizes: tCommon('MultipleSizes'),
                  })
                }
                handleClick={() =>
                  handleProductClick(productItem.id, productItem.slug, index)
                }
                brandName={productItem.brand_name}
              />
            </li>
          ))}
      </ScrollableItemRow>
    </section>
  );
}
